import { AlertCircle, AlertTriangle } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { useContas, useVinculosDoBeneficiario } from "@/api/consultas";
import type { Pendencia, Prontidao } from "@/api/contrato";
import { Dica } from "@/componentes/Dica";
import { Esqueleto } from "@/componentes/Esqueleto";
import { FaixaDeErro } from "@/componentes/FaixaDeErro";
import { banco } from "@/formato";
import { useSessao } from "@/sessao/useSessao";
import {
  acaoDaPendencia,
  DICA_DA_PENDENCIA,
  matizDaPendencia,
  pendenciasDaLista,
  ROTULO_DA_PENDENCIA,
} from "./pendencias";

/*
 * O que falta para o cedente emitir, entre o cabeçalho e as abas.
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ UMA LINHA CURTA E UM BOTÃO, E NÃO UMA EXPLICAÇÃO.                        │
 * │                                                                          │
 * │ O fato em até cinco palavras, o porquê em `Dica` e a ação na mesma       │
 * │ linha. Barra de progresso, "passo 2 de 5" e parabéns não existem aqui    │
 * │ (doc 06a §2), e o cedente pronto não ocupa espaço nenhum: quem diz       │
 * │ "pronto" é o selo.                                                       │
 * └──────────────────────────────────────────────────────────────────────────┘
 *
 * Sem a prontidão, a tela não assume nada: nem lista vazia, nem pronto.
 */
const FALHA_NO_CALCULO = new Error("Não foi possível calcular as pendências.");

const ESTILO = {
  falha: { Icone: AlertTriangle, cor: "text-falha" },
  transito: { Icone: AlertCircle, cor: "text-transito" },
} as const;

/** A forma do `Botao` secundário: a ação navega, e continua abrindo em aba nova. */
const LINK_DE_ACAO =
  "inline-flex h-8 items-center justify-center rounded-controle border border-fio-forte bg-superficie-2 px-3 text-corpo font-medium text-texto transition-colors hover:bg-fio";

/*
 * De qual conta ou aplicação a linha fala: cada conta e cada aplicação são um ramo, e
 * duas linhas `Sem chave de emissão` iguais não dizem qual abrir. As duas consultas são
 * as das abas, no mesmo cache, e só saem quando há o que identificar. Sem resposta, a
 * linha fica só com o fato, que já é o que importa.
 */
function useAlvoDaPendencia(pessoaID: string, pendencias: readonly Pendencia[]) {
  const { cliente } = useSessao();
  const temConta = pendencias.some((pendencia) => pendencia.conta_id !== undefined);
  const temPar = pendencias.some((pendencia) => pendencia.sistema_id !== undefined);

  const contas = useContas(cliente, temConta ? pessoaID : null).data?.contas;
  const vinculos = useVinculosDoBeneficiario(cliente, temPar ? pessoaID : null).data?.vinculos;

  return ({ conta_id, convenio_id, sistema_id }: Pendencia): string | undefined => {
    if (sistema_id !== undefined) {
      return vinculos?.find((vinculo) => vinculo.sistema_id === sistema_id)?.sistema_nome;
    }

    const conta = contas?.find((candidata) => candidata.conta_id === conta_id);
    if (conta === undefined) return undefined;

    const convenio = conta.convenios.find((candidato) => candidato.id === convenio_id);
    return convenio === undefined
      ? `${banco(conta.banco)} · ${conta.agencia}/${conta.conta}`
      : `${conta.banco}/${convenio.numero}`;
  };
}

export function ListaDePendencias({
  pessoaID,
  prontidao,
  falhou,
}: {
  pessoaID: string;
  /** `undefined` enquanto carrega, ou quando a leitura falhou. */
  prontidao: Prontidao | undefined;
  falhou: boolean;
}) {
  const { pode } = useSessao();
  const estado: unknown = useLocation().state;
  const pendencias = pendenciasDaLista(prontidao);
  const alvoDe = useAlvoDaPendencia(pessoaID, pendencias);

  if (falhou) return <FaixaDeErro erro={FALHA_NO_CALCULO} />;

  if (prontidao === undefined) {
    return (
      <div role="status" aria-busy="true" aria-label="Calculando pendências">
        <Esqueleto className="h-[42px]" />
      </div>
    );
  }

  if (pendencias.length === 0) return null;

  // `Sem endereço` e `Sem contato` são a mesma ação, e o botão aparece na primeira. O rótulo e o
  // destino contam juntos: a mesma ação em aplicações diferentes continua com um botão cada.
  const oferecidas = new Set<string>();

  return (
    <ul
      aria-label="Pendências"
      className="divide-y divide-fio rounded-cartao border border-fio bg-superficie"
    >
      {pendencias.map((pendencia) => {
        const { codigo, bloqueia, conta_id, convenio_id, sistema_id } = pendencia;
        const { Icone, cor } = ESTILO[matizDaPendencia(pendencia)];
        const acao = acaoDaPendencia(pessoaID, pendencia);
        const alvo = alvoDe(pendencia);
        const oferta = `${acao.rotulo} ${acao.para ?? ""}`;
        const repetida = oferecidas.has(oferta);
        oferecidas.add(oferta);

        return (
          <li
            key={`${codigo}-${conta_id ?? ""}-${convenio_id ?? ""}-${sistema_id ?? ""}`}
            className="flex min-h-10 items-center gap-2 px-3 py-1"
          >
            {/* Ícone e texto, nunca só cor (doc 06a §7). */}
            <Icone aria-hidden="true" className={`size-4 shrink-0 ${cor}`} />
            <span className={`shrink-0 ${cor}`}>
              {!bloqueia && <span className="sr-only">Aviso: </span>}
              {ROTULO_DA_PENDENCIA[codigo]}
            </span>
            {alvo !== undefined && (
              <span className="dados min-w-0 truncate text-[12px] text-texto-2">{alvo}</span>
            )}
            <Dica texto={DICA_DA_PENDENCIA[codigo]} />

            <div className="ml-auto shrink-0">
              {/* A API recusa de qualquer jeito: esconder é só não oferecer. */}
              {acao.para !== null && !repetida && (!acao.escreve || pode("admin")) && (
                // O `state` guarda de que lista o operador veio, para o "« voltar".
                <Link to={acao.para} state={estado} className={LINK_DE_ACAO}>
                  {acao.rotulo}
                </Link>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
}
